/**
 * DP-3214: the two colour-sort decisions the manifest list makes, kept out of the components so
 * both sides of the `backendColorRules` flag are unit-testable without rendering a panel.
 */

/**
 * Whether to re-sort the loaded page by colour in the browser.
 *
 * False once backend colours are on: the query has already ordered the FULL result set by
 * stops.indicatorRank, so re-sorting here would be worse than redundant. The client sort ranks by
 * rule priority derived from a colour rather than the server's per-stop stamp, so it can disagree
 * with the ordering the server just applied — and it can only ever reorder the loaded page, which
 * is the limitation this ticket removes.
 */
export const shouldSortManifestsOnClient = ({
  backendColorRulesEnabled,
  colorSortEnabled,
  rulesLoading,
  sortByColorEnabled
}: {
  backendColorRulesEnabled?: boolean;
  colorSortEnabled?: boolean;
  rulesLoading?: boolean;
  sortByColorEnabled?: boolean;
}): boolean => !backendColorRulesEnabled && !rulesLoading && Boolean(colorSortEnabled) && Boolean(sortByColorEnabled);

/**
 * Whether to show "Color sorting is limited to the current page".
 *
 * That statement is true of the client-side sort and false once the server does the ordering, so
 * the banner retires with the flag rather than lingering as a stale warning. It is also pointless
 * on a single page, which is why the pageCount check predates this ticket.
 */
export const shouldShowColorSortWarning = ({
  backendColorRulesEnabled,
  sortByColorEnabled,
  pageCount
}: {
  backendColorRulesEnabled?: boolean;
  sortByColorEnabled?: boolean;
  pageCount?: number;
}): boolean => !backendColorRulesEnabled && Boolean(sortByColorEnabled) && (pageCount ?? 0) > 1;
